import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { CONFIRM_RESULT, ConfirmationDialogComponent, ConfirmOptions, RMS_DIALOG_SM_CONFIG } from '@rms/sdk/dialog';
import { map, Observable } from 'rxjs';

import { ACTION_NOTIFICATION_DATA, ActionNotificationComponent } from './action-notification.component';
import { DEFAULT_SNACKBAR_CONFIG, ERROR_SNACKBAR_CONFIG } from './notification.models';

/**
 * Application notifications, confirmation and more
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);

  private persistentOverlayRef: OverlayRef | null = null;

  public notify(messageToken: string, actionToken: string, config?: MatSnackBarConfig, onAction?: () => void): void {
    this.translate(messageToken, actionToken).subscribe(([message, action]) => {
      const snackBarRef = this.snackBar.open(message, action, config ? config : DEFAULT_SNACKBAR_CONFIG);

      if (onAction) {
        snackBarRef.onAction().subscribe(() => onAction());
      }
    });
  }

  public error(messageToken: string, actionToken = 'common.ok', config?: MatSnackBarConfig): void {
    this.translate(messageToken, actionToken).subscribe(([message, action]) => {
      this.snackBar.open(message, action, config ? config : ERROR_SNACKBAR_CONFIG);
    });
  }

  public confirm(options: ConfirmOptions): Observable<CONFIRM_RESULT> {
    const dialog = this.dialog.open(ConfirmationDialogComponent, {
      ...RMS_DIALOG_SM_CONFIG,
      panelClass: [...(RMS_DIALOG_SM_CONFIG.panelClass as string[]), 'rms-dialog-confirm'],
      data: options,
    });

    return dialog.afterClosed();
  }

  // Looks like the error snackbar but lives in its own overlay, which MatSnackBar never touches, so later
  // snackbars can't dismiss or replace it. Stays until the action is clicked; only one at a time.
  public notifyPersistent(messageToken: string, actionToken: string, onAction: () => void): void {
    if (this.persistentOverlayRef) {
      return;
    }

    const overlayRef = this.overlay.create({
      positionStrategy: this.overlay.position().global().centerHorizontally().top('8px'),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      hasBackdrop: false,
      panelClass: [
        'rms-action-notification',
        'mat-mdc-snack-bar-container',
        ...(ERROR_SNACKBAR_CONFIG.panelClass as string[]),
      ],
    });

    this.persistentOverlayRef = overlayRef;

    this.translate(messageToken, actionToken).subscribe(([message, action]) => {
      const injector = Injector.create({
        parent: this.injector,
        providers: [
          {
            provide: ACTION_NOTIFICATION_DATA,
            useValue: {
              message,
              action,
              onAction: () => {
                overlayRef.dispose();
                this.persistentOverlayRef = null;
                onAction();
              },
            },
          },
        ],
      });

      overlayRef.attach(new ComponentPortal(ActionNotificationComponent, null, injector));
    });
  }

  private translate(messageToken: string, actionToken: string): Observable<[string, string]> {
    return this.translateService
      .get([messageToken, actionToken])
      .pipe(map((data): [string, string] => [String(data[messageToken]), String(data[actionToken])]));
  }
}
